import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Position, useEdges, useReactFlow } from 'reactflow';
import '../../assets/styles/animatedBorder.scss';

import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Switch,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import {
  CaretDown,
  CaretUp,
  Gear,
  Info,
  Link,
  XCircle,
} from '@phosphor-icons/react';
import { useForm } from 'react-hook-form';
import { useNotificationStore } from '../../store/storeNotifications';

import AiIcon from '../../assets/icons/AiIcon';
import { useInputSizeTracking } from '../../hooks/useInputSizeTracking';
import { axiosInstance } from '../../service/ApiService';
import { FlowService } from '../../service/FlowService';
import { FluxService } from '../../service/FluxService';
import { UserConfigService } from '../../service/UserConfigService';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { useSessionStore } from '../../store/storeSessions';
import { NodeProps } from '../../util/Types/NodeProps';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { checkExecuteTrigger } from '../../util/checkExecute';
import { createSystemTimestamp } from '../../util/dateUtil';
import { executeErrorHandle } from '../../util/executeErrorHandle';
import { isActiveEdge } from '../../util/findActiveHandle';
import { generateCustomID } from '../../util/generateCustomId';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import { useObserveElementWidth } from '../../util/useObserveElement';
import {
  aiSystemToModelField,
  commandTextNodeDefaultValues,
  getDefaultModelsFromTechnologies,
  getDeprecatedModelMigration,
  modelTokenLimits,
  systemValueMap,
} from '../AiConfigs/constants';
import SettingsModal from '../AiTextGenerator/SettingsModal/SettingsModal';
import { aiGeneratorInputs } from '../AiTextGenerator/constants';
import { commandTextNodeResponses } from '../AiTextGenerator/responses';
import GenerateFileLinkModal from '../GenerateFileLinkModal/GenerateFileLinkModal';

import TagInput from '../Test/TagInput';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import InputHandle from '../UI/InputHandle/InputHandle';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import OutputTable from '../UI/OutputTable/OutputTable';
import OutputTextarea from '../UI/OutputTextarea/OutputTextarea';

export const sourceTypes = {
  promptIn: 'prompt',
  contentIn: 'content',
  instructionIn: 'instruction',
  personaIn: 'persona',
};

// WordCount component
export function WordCount({
  text,
  props,
}: {
  text: string | null | undefined;
  props?: SxProps<Theme>;
}) {
  if (!text || typeof text !== 'string') return null;

  const trimmedText = text.trim();
  const wordCount = trimmedText.split(/\s+/).filter(Boolean).length;

  return (
    <Typography
      color={'#98A2B3'}
      fontSize={'12px'}
      lineHeight={'0'}
      sx={{ ...props }}
    >
      {wordCount} {wordCount === 1 ? 'Word' : 'Words'}
    </Typography>
  );
}

function AiTextGenerator({ id, data, type, selected }: NodeProps) {
  const { user, technologies, companySpaceTechnologies } = useAuthStore(
    state => state,
  );

  const { height: maxOutputHeight, ref: inputRef } =
    useObserveElementWidth<HTMLDivElement>();

  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [isAutoFinding, setIsAutoFinding] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [hasToExecute, setHasToExecute] = useState(true);
  const [executeCounter, setExecuteCounter] = useState(0);

  const previewResponses = data?.previewResponses || [];

  const getCurrentTextValue = () => {
    if (previewResponses.length > 0 && data.previewIndex !== undefined) {
      const index = data.previewIndex;
      if (index >= 0 && index < previewResponses.length) {
        return previewResponses[index];
      }
    }
    return data.text || '';
  };

  const [whoIsChanging, setWhoIsChanging] = useState<{
    value: string | boolean;
    name: string;
    isChangingDirectly: boolean;
  }>({
    value: '',
    name: '',
    isChangingDirectly: false,
  });
  const { setNodes: setNodeStateReactFlow, getNode, getNodes, setEdges } = useReactFlow();

  const { spaceId: flowSpaceId } = useFlowsStore(state => state);

  const backendDefaults = useMemo(
    () =>
      getDefaultModelsFromTechnologies(
        flowSpaceId && companySpaceTechnologies.length > 0
          ? companySpaceTechnologies
          : technologies,
      ),
    [technologies, companySpaceTechnologies, flowSpaceId],
  );

  const [formData, setFormData] = useState({
    ...commandTextNodeDefaultValues,
    ...backendDefaults,
    ...data,
  });

  const [showGenerateFileLinkModal, setShowGenerateFileLinkModal] =
    useState(false);

  // Memory/Chat Mode state
  const [entriesAnchorEl, setEntriesAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [settingsAnchorEl, setSettingsAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  const openEntriesPopover = Boolean(entriesAnchorEl);
  const popOverId = openEntriesPopover ? 'simple-popover' : undefined;

  const openSettingsPopover = Boolean(settingsAnchorEl);
  const popOverSettingsId = openSettingsPopover ? 'simple-popover' : undefined;

  const [memoryFormData, setMemoryFormData] = useState({
    selectedType: 'Load All Entries',
    selectedNumber: 50,
    selectedIndex: 5,
    selectedChat: 'default',
    clearOnExecution: false,
  });

  const { handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      ...memoryFormData,
      ...(data.selectedType && { selectedType: data.selectedType }),
      ...(data.selectedNumber && { selectedNumber: data.selectedNumber }),
      ...(data.selectedIndex && { selectedIndex: data.selectedIndex }),
      ...(data.selectedChat && { selectedChat: data.selectedChat }),
      ...(data.clearOnExecution !== undefined && {
        clearOnExecution: data.clearOnExecution,
      }),
    },
  });

  const [userSessionChat, setUserSessionChat] = useState<
    {
      id: string;
      name: string;
      memories: {
        role: string;
        content: string;
        timestamp: string;
        timeline?: string;
      }[];
    }[]
  >([]);

  const { sessions, activeSession, getSessionById } = useSessionStore(
    state => state,
  );

  const getCurrentSessionName = (sessionId: string): string => {
    const session = getSessionById(sessionId);
    if (session) {
      return session.name;
    }

    const chatSession = userSessionChat.find(chat => chat.id === sessionId);
    return (
      chatSession?.name || (sessionId === 'default' ? 'Primary' : sessionId)
    );
  };

  const [globalStarterSessionId, setGlobalStarterSessionId] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!activeSession?.id) {
      const nodes = getNodes();
      const globalStarterNode = nodes.find(
        (node: any) =>
          node.type === 'globalStarter' ||
          node.id.includes('flow-global-starter'),
      );
      const globalSession = globalStarterNode?.data?.selectedChat || 'default';
      setGlobalStarterSessionId(globalSession);
    } else {
      setGlobalStarterSessionId(null);
    }
  }, [data, activeSession?.id]);

  const currentSessionId = useMemo(() => {
    if (activeSession?.id) {
      return activeSession.id;
    }
    return globalStarterSessionId || 'default';
  }, [activeSession?.id, globalStarterSessionId]);

  const currentSessionName = useMemo(
    () => getCurrentSessionName(currentSessionId),
    [currentSessionId, sessions, userSessionChat],
  );

  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [whoIsChangingMemory, setWhoIsChangingMemory] = useState<{
    value: string | boolean;
    name: string;
  }>({
    value: '',
    name: '',
  });
  const [footerExpanded, setFooterExpanded] = useState(true);

  const edges = useEdges();
  const { setSchema, flowId, schema, setSaving, spaceId, setNodeState } = useFlowsStore(
    state => state,
  );
  const setNotifications = useNotificationStore(state => state.setNotification);

  const inputSize = useInputSizeTracking({
    nodeId: id,
    inputSize: data?.inputSize,
    collapsed: data?.collapsed,
    edges,
    flowId,
    setNodeState,
    setSaving,
    setSchema,
  });

  const debounceTimeoutRef = useRef<any>(null);

  const syncSessions = () => {
    if (!data.memory) return;

    const uncreatedSessions = sessions?.filter(
      session =>
        !(data.userSessionsChat || []).some(
          (chat: any) => chat.id === session.id,
        ),
    );

    if (uncreatedSessions.length > 0) {
      const newChat = [
        ...uncreatedSessions.map((session: any) => ({
          id: session.id,
          name: session.name,
          memories: [],
        })),
        ...(data.userSessionsChat || []),
      ];

      onChangeMemory({ target: { name: 'userSessionsChat', value: newChat } });
    }
  };

  useEffect(() => {
    syncSessions();
  }, [data.memory, sessions]);

  useEffect(() => {
    const systemsToUse =
      spaceId && companySpaceTechnologies.length > 0
        ? companySpaceTechnologies
        : technologies;

    if (!systemsToUse || systemsToUse.length === 0) return;

    const migration = getDeprecatedModelMigration(
      formData.AiSystem,
      formData,
      systemsToUse,
    );

    if (migration) {
      onChange({
        target: { name: migration.fieldName, value: migration.newModel },
      });
    }
  }, [technologies, companySpaceTechnologies, spaceId, formData.AiSystem]);

  useEffect(() => {
    if (!backendDefaults || Object.keys(backendDefaults).length === 0) return;

    const updates: Record<string, string> = {};
    for (const [field, value] of Object.entries(backendDefaults)) {
      if (value && !data[field]) {
        updates[field] = value;
      }
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  }, [backendDefaults]);

  useEffect(() => {
    setUserSessionChat(data.userSessionsChat || []);
  }, [data.userSessionsChat]);

  // Effect to manage memory entry in contentData array
  useEffect(() => {
    if (!data.memory) {
      // Memory is disabled - remove memory entry from contentData
      const currentContentData = data.contentData || [];
      const updatedContentData =
        removeMemoryFromContentData(currentContentData);

      if (updatedContentData.length !== currentContentData.length) {
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          objectData: {
            contentData: updatedContentData,
          },
          setSchema,
        });
      }
      return;
    }

    // Memory is enabled - add/update memory entry in contentData
    const memories = getCurrentSessionMemories();
    const selectedType = data.selectedType || 'Load All Entries';
    const selectedNumber = data.selectedNumber || 50;
    const selectedIndex = data.selectedIndex || 5;

    // Filter memories based on settings
    const filteredMemories = filterMemoriesForContentData(
      memories,
      selectedType,
      selectedNumber,
      selectedIndex,
    );

    // Update contentData
    const currentContentData = data.contentData || [];
    const updatedContentData = updateContentDataWithMemory(
      currentContentData,
      filteredMemories,
      id,
    );

    // Only update if contentData changed
    const currentMemoryEntry = currentContentData.find(
      (item: any) => item.label === 'Memory' && item.id === id,
    );
    const newMemoryText =
      filteredMemories.length > 0 ? JSON.stringify(filteredMemories) : null;

    const needsUpdate =
      (!currentMemoryEntry && newMemoryText !== null) || // Need to add
      (currentMemoryEntry && currentMemoryEntry.text !== newMemoryText) || // Need to update
      (currentMemoryEntry && newMemoryText === null); // Need to remove

    if (needsUpdate) {
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        flowId,
        objectData: {
          contentData: updatedContentData,
        },
        setSchema,
      });
    }
  }, [
    data.memory,
    currentSessionId,
    data.userSessionsChat,
    data.selectedType,
    data.selectedNumber,
    data.selectedIndex,
    data.contentData,
  ]);

  useEffect(() => {
    setMemoryFormData({
      selectedType: data.selectedType || 'Load All Entries',
      selectedNumber: data.selectedNumber || 50,
      selectedIndex: data.selectedIndex || 5,
      clearOnExecution: data.clearOnExecution || false,
      selectedChat: data.selectedChat || 'default',
    });
  }, [
    data.selectedType,
    data.selectedNumber,
    data.selectedIndex,
    data.clearOnExecution,
    data.selectedChat,
  ]);
  const prevPromptEdgeIdsRef = useRef<Set<string>>(new Set());
  const hasConvertedPromptRef = useRef<boolean>(false);
  const hasConvertedToInputRef = useRef<boolean>(false);

  const convertContentBoxToInput = useCallback(() => {
    const PROMPT_HANDLE_ID = 'promptIn';
    const promptEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === PROMPT_HANDLE_ID,
    );
    const isConnected = promptEdges.length > 0;

    const existingPromptData = data?.promptData || [];
    const customContentBoxes = existingPromptData.filter(
      (item: any) => item.id?.startsWith('custom_'),
    );

    if (customContentBoxes.length === 1 && !isConnected && !formData.prompt?.trim()) {
      const contentBoxText = customContentBoxes[0].text;
      const edgeContentBoxes = existingPromptData.filter(
        (item: any) => !item.id?.startsWith('custom_'),
      );

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        flowId,
        setSaving,
        objectData: {
          promptData: edgeContentBoxes,
          prompt: contentBoxText,
        },
        setSchema,
      });

      setFormData((prev: any) => ({
        ...prev,
        prompt: contentBoxText,
      }));

      hasConvertedToInputRef.current = true;
    }
  }, [data?.promptData, formData.prompt, edges, id, flowId, setNodeState, setSaving, setSchema]);

  useEffect(() => {
    const PROMPT_HANDLE_ID = 'promptIn';
    const promptEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === PROMPT_HANDLE_ID,
    );

    const currentEdgeIds = new Set(promptEdges.map(edge => edge.id));
    const prevEdgeIds = prevPromptEdgeIdsRef.current;
    const hasNewEdge = Array.from(currentEdgeIds).some(id => !prevEdgeIds.has(id));
    const hasRemovedEdge = Array.from(prevEdgeIds).some(id => !currentEdgeIds.has(id));
    const isConnected = promptEdges.length > 0;
    const wasConnected = prevEdgeIds.size > 0;

    if (!isConnected && wasConnected) {
      hasConvertedPromptRef.current = false;
      convertContentBoxToInput();
    }

    if (hasRemovedEdge && !isConnected) {
      convertContentBoxToInput();
    }

    if (!hasNewEdge || !isConnected || hasConvertedPromptRef.current) {
      prevPromptEdgeIdsRef.current = currentEdgeIds;
      return;
    }

    const promptText = (formData.prompt || data?.prompt || '').trim();
    if (!promptText) {
      prevPromptEdgeIdsRef.current = currentEdgeIds;
      return;
    }

    const existingPromptData = data?.promptData || [];
    const isDuplicate = existingPromptData.some(
      (item: any) => item.text === promptText && item.id?.startsWith('custom_'),
    );

    if (isDuplicate) {
      prevPromptEdgeIdsRef.current = currentEdgeIds;
      return;
    }

    const newContentBox = {
      label: 'Custom',
      text: promptText,
      id: 'custom_' + generateCustomID(),
    };

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      setSaving,
      objectData: {
        promptData: [newContentBox, ...existingPromptData,],
        prompt: '',
      },
      setSchema,
    });

    setFormData((prev: any) => ({
      ...prev,
      prompt: '',
    }));

    hasConvertedPromptRef.current = true;
    hasConvertedToInputRef.current = false;
    prevPromptEdgeIdsRef.current = currentEdgeIds;
  }, [edges, id, formData.prompt, data?.prompt, data?.promptData, flowId, setNodeState, setSaving, setSchema, convertContentBoxToInput]);

  useEffect(() => {
    if (hasConvertedToInputRef.current) {
      return;
    }

    const PROMPT_HANDLE_ID = 'promptIn';
    const promptEdges = edges.filter(
      edge => edge.target === id && edge.targetHandle === PROMPT_HANDLE_ID,
    );
    const isConnected = promptEdges.length > 0;

    const existingPromptData = data?.promptData || [];
    const customContentBoxes = existingPromptData.filter(
      (item: any) => item.id?.startsWith('custom_'),
    );

    if (customContentBoxes.length === 1 && !isConnected && !formData.prompt?.trim()) {
      convertContentBoxToInput();
    }
  }, [data?.promptData, edges, id, formData.prompt, convertContentBoxToInput]);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (data?.flowTrigger?.id && !data?.canceled && !data?.paused) {
      if (hasToExecute) {
        debounceTimeoutRef.current = setTimeout(() => {
          setHasToExecute(false);
          onSubmit();
        }, 1000);
      }
    }

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [hasToExecute, data, formData]);

  const onSubmit = async () => {
    setIsLoading(true);

    try {
      const text = ['prompt', 'persona', 'instruction', 'content'];
      let newFormData: any = { ...formData };

      text.forEach(t => {
        if (data[t + 'Data']?.length) {
          if (t === 'content' || t === 'prompt' || t === 'persona' || t === 'instruction') {
            newFormData[t] = data[t + 'Data']
              ?.map((item: { text: string; label?: string }) => {
                const label = item.label || '';
                return label ? `_____${label}_____\n${item.text}` : item.text;
              })
              .join('\n');
          } else {
            newFormData[t] = data[t + 'Data']
              ?.map((item: { text: string }) => item.text)
              .join(' \n');
          }
        }
      });

      const systemsForRequests =
        flowSpaceId && companySpaceTechnologies.length > 0
          ? companySpaceTechnologies
          : technologies;

      const response = await commandTextNodeResponses(
        newFormData,
        schema,
        flowId,
      );

      let responses = [...(data.previewResponses || [])];
      let updatedFileLinks = [...(data?.fileLinks || [])];

      if (responses.length === 10) {
        responses.shift();
      }

      if (updatedFileLinks.length === 10 && data.saveOutput) {
        updatedFileLinks.shift();
      }

      let fileLink;
      if (data.saveOutput) {
        const { data: res } = await axiosInstance.post('/upload/text', {
          text: response?.data?.message || '',
          fileType: data.fileType,
        });
        fileLink = res.data;
      }

      const filteredFileLinks = fileLink
        ? [...updatedFileLinks.filter(Boolean), fileLink]
        : updatedFileLinks.filter(Boolean);

      const promptContent = [
        newFormData.prompt,
        newFormData.content,
        newFormData.instruction,
        newFormData.persona,
      ]
        .filter(Boolean)
        .join('\n');

      let updatedUserSessionsChat = data.userSessionsChat || [];
      if (data.memory && promptContent && response?.data?.message) {
        const currentSession = currentSessionId;
        const timestamp = createSystemTimestamp();

        let sessionIndex = updatedUserSessionsChat.findIndex(
          (chat: any) => chat.id === currentSession,
        );

        if (sessionIndex === -1) {
          const session = getSessionById(currentSession);
          const sessionName =
            session?.name ||
            (currentSession === 'default' ? 'Primary' : currentSession);
          updatedUserSessionsChat.push({
            id: currentSession,
            name: sessionName,
            memories: [],
          });
          sessionIndex = updatedUserSessionsChat.length - 1;
        }

        const newMemories = [
          {
            role: 'user',
            content: newFormData.prompt,
            timestamp,
          },
          {
            role: 'assistant',
            content: response?.data?.message,
            timestamp,
          },
        ];

        updatedUserSessionsChat[sessionIndex] = {
          ...updatedUserSessionsChat[sessionIndex],
          memories: [
            ...(updatedUserSessionsChat[sessionIndex].memories || []),
            ...newMemories,
          ],
        };
      }

      setFormData({
        ...newFormData,
        text: response?.data?.message,
        logs: response?.data?.logs ?? '',
        previewResponses: [...responses, response?.data?.message],
        previewIndex: responses.length,
        fileLinks: filteredFileLinks,
        linkIndex: filteredFileLinks.length,
        fileLink,
      });

      // Update contentData with new memories if memory is enabled
      let updatedContentData = data.contentData || [];
      if (data.memory && updatedUserSessionsChat.length > 0) {
        const currentSession = currentSessionId;
        const sessionData = updatedUserSessionsChat.find(
          (chat: any) => chat.id === currentSession,
        );
        if (sessionData?.memories && sessionData.memories.length > 0) {
          const selectedType = data.selectedType || 'Load All Entries';
          const selectedNumber = data.selectedNumber || 50;
          const selectedIndex = data.selectedIndex || 5;

          const filteredMemories = filterMemoriesForContentData(
            sessionData.memories,
            selectedType,
            selectedNumber,
            selectedIndex,
          );

          updatedContentData = updateContentDataWithMemory(
            updatedContentData,
            filteredMemories,
            id,
          );
        }
      }

      // Update local state immediately so the table updates right away
      if (data.memory && updatedUserSessionsChat.length > 0) {
        setUserSessionChat(updatedUserSessionsChat);
      }

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState: setNodeStateReactFlow,
        id,
        flowTriggerData: checkExecuteTrigger(data, id),
        flowId,
        objectData: {
          text: response?.data?.message,
          logs: response?.data?.logs ?? '',
          previewResponses: [...responses, response?.data?.message],
          previewIndex: responses.length,
          fileLinks: filteredFileLinks,
          linkIndex: filteredFileLinks.length,
          fileLink,
          userSessionsChat: updatedUserSessionsChat,
          contentData: updatedContentData,
          usedTokens: data?.flowTrigger?.id
            ? response?.data?.usedTokens
            : undefined,
          nodeUsedTokens: response?.data?.usedTokens,
        },
        changeType: 'execute',
        setSchema,
        setSaving,
      });

      // Fetch updated flow data to refresh node state with latest backend data
      // This ensures memories and other data are properly synced after save
      setTimeout(async () => {
        try {
          const currentVersionId = new URLSearchParams(window.location.search).get('versionId') || undefined;
          const flowResponse = await FlowService.getFlow(flowId, undefined, undefined, currentVersionId);
          const updatedNode = flowResponse.data?.nodes?.find(
            (n: any) => n.id === id,
          );
          if (updatedNode) {
            setNodeState(nodes =>
              nodes.map(nd => (nd.id === id ? updatedNode : nd)),
            );
          }
        } catch (error) {
          console.error('Failed to refresh flow data:', error);
        }
      }, 1000); // Wait 1 second to ensure backend has saved the data

      setExecuteCounter(0);

      setTimeout(() => {
        setHasToExecute(true);
      }, 2500);
    } catch (error: any) {
      setNotifications({
        type: 'error',
        message: error.message ?? 'Something went wrong!',
      });

      executeErrorHandle(
        executeCounter,
        setExecuteCounter,
        setHasToExecute,
        data,
        edges,
        setNodeState,
        setSaving,
        id,
        flowId,
        setSchema,
        setNotifications,
        error,
        [...(user?.spaceObjects || []), ...(user?.objects || [])],
      );
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const checkWordCount = () => {
    const text = ['prompt', 'persona', 'instruction', 'content'];

    let newFormData: any = { ...formData, ...data };

    text.forEach(t => {
      if (newFormData[t + 'Data']?.length) {
        newFormData[t] = newFormData[t + 'Data']
          ?.map((item: { text: string }) => item.text)
          .join(' \n');
      }
    });

    return newFormData;
  };

  const setFormValue = (name: string, value: string | boolean, data?: any) => {
    setFormData({
      ...formData,
      [name]: value,
      ...data,
    });
  };

  const onChange = (e: { target: { name: string; value: string | boolean } }) => {
    if (e.target.name === 'anthropicModel') {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
        anthropicToken: e.target.value !== 'claude-35-sonnet' ? 4096 : 8192,
      });

      setWhoIsChanging({
        name: e.target.name,
        value: e.target.value,
        isChangingDirectly: true,
      });

      return;
    }

    if (e.target.name === 'aiModel') {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
        aiTokens:
          modelTokenLimits.find(item => item.model === e.target.value)
            ?.tokens || 4096,
      });

      setWhoIsChanging({
        name: e.target.name,
        value: e.target.value,
        isChangingDirectly: true,
      });

      return;
    }

    if (e.target.name === 'AiSystem' && e.target.value === 'XAi') {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
        aiTokens: '8000',
      });

      setWhoIsChanging({
        name: e.target.name,
        value: e.target.value,
        isChangingDirectly: true,
      });

      return;
    }

    setFormValue(e.target.name, e.target.value);

    setWhoIsChanging({
      name: e.target.name,
      value: e.target.value,
      isChangingDirectly: true,
    });
  };

  const onChangePlacement = (name: string, value: string) => {
    setWhoIsChanging({
      name: name,
      value: value,
      isChangingDirectly: true,
    });
  };

  useDebounceEffect(
    () => {
      if (whoIsChanging.isChangingDirectly) {
        setWhoIsChanging({
          value: '',
          name: '',
          isChangingDirectly: false,
        });

        let object = {};

        if (whoIsChanging.name === 'aiModel') {
          object = {
            aiTokens:
              modelTokenLimits.find(item => item.model === whoIsChanging.value)
                ?.tokens || 4096,
          };
        }
        if (whoIsChanging.name === 'anthropicModel') {
          object = {
            anthropicToken: 4096,
          };
        }

        if (whoIsChanging.name === 'type') {
          object = {
            modelName:
              whoIsChanging.value === 'text'
                ? 'models/text-bison-001'
                : 'models/chat-bison-001',
          };
        }

        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          setSaving,
          id,
          flowId,
          objectData: {
            [whoIsChanging.name]: whoIsChanging.value,
            ...object,
          },
          setSchema,
        });
      }
    },
    [whoIsChanging],
    300,
  );

  const onPreview = (type: string) => {
    if (data?.text !== undefined && previewResponses?.length > 0) {
      const findIndex =
        data?.previewIndex !== undefined ? data?.previewIndex : 0;

      if (findIndex !== -1) {
        const nextIndex = type === 'next' ? findIndex + 1 : findIndex - 1;

        if (nextIndex >= 0 && nextIndex < previewResponses.length) {
          nodeDataAutoSaveDynamic({
            newEdges: edges,
            setNodeState,
            id,
            flowId,
            setSaving,
            changeType: 'changingPreviewIndex',
            objectData: {
              text: previewResponses[nextIndex],
              previewIndex: nextIndex,
            },
            setSchema,
          });
        }
      }
    }
  };

  const onChangeOnExecute = (e: any) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      setSaving,
      objectData: {
        clearOnExecution: e.target.checked,
      },
      setSchema,
    });
  };

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;

  const changeModelBasedOnSystem = (system: string) => {
    switch (system) {
      case 'OpenAI':
        return formData.aiModel;
      case 'Gemini':
        return formData.geminiModel;
      case 'openSources':
        return formData.openSourceModelName;
      case 'perplexityAi':
        return formData.perplexityType;
      case 'anthropicAi':
        return formData.anthropicModel;
      case 'Groq':
        return formData.groqModel;
      case 'XAi':
        return formData.xaiModel;
      default:
        break;
    }
  };

  const applyAutoFindResult = async (text: string) => {
    try {
      const cleaned = text.replace(/```(?:json)?\s*/g, '').replace(/```/g, '').trim();
      const modelResult = JSON.parse(cleaned);
      const { modelName, provider } = modelResult;

      if (!modelName || !provider) return;

      const aiSystem = systemValueMap[provider];
      if (!aiSystem) return;

      const modelField = aiSystemToModelField[aiSystem];
      if (!modelField) return;

      setFormData(prev => ({
        ...prev,
        AiSystem: aiSystem,
        [modelField]: modelName,
      }));

      const dataToSave = {
        ...data,
        AiSystem: aiSystem,
        [modelField]: modelName,
        isEmptyState: false,
      };
      const updatedData = { ...dataToSave, _remountKey: Date.now() };

      setNodeStateReactFlow(prev =>
        prev.map(n => (n.id === id ? { ...n, data: updatedData } : n)),
      );

      setSaving({
        finished: false,
        lastSaved: new Date().toISOString(),
        status: 'loading',
        flowId: flowId!,
      });

      try {
        await FlowService.updateNodeData(flowId!, {
          id,
          data: dataToSave,
          changeType: 'default',
        });
      } catch {
      }

      setSaving({
        finished: true,
        lastSaved: new Date().toISOString(),
        status: 'saved',
        flowId: flowId!,
      });
    } catch {
      setNotifications({
        message: 'Failed to apply auto-find result',
        type: 'error',
      });
    }
  };

  const handleAutoFind = async () => {
    const autoModelFlowId = import.meta.env.VITE_AUTO_MODEL_FLOW_ID || '';
    if (!autoModelFlowId || isAutoFinding) return;

    setIsAutoFinding(true);

    try {
      let userPreferences = '';
      try {
        const configResponse = await UserConfigService.getConfigs();
        if (configResponse?.data) {
          userPreferences = JSON.stringify(configResponse.data);
        }
      } catch {
        userPreferences = '';
      }

      const currentNode = getNode(id);
      const connectedEdges = edges.filter(e => e.source === id || e.target === id);
      const flowSummary = getNodes().find(n => n.type === 'globalStarter')?.data?.summary || '';
      const nodeJson = currentNode
        ? { id: currentNode.id, type: currentNode.type, position: currentNode.position, data: currentNode.data }
        : { id, type, data };
      const contextData = {
        flowSummary,
        flowId: flowId || '',
        sessionId: '',
        nodeJson,
        connectedEdges: connectedEdges.map(e => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle })),
      };

      const variableInputs = [
        {
          inputId: 'varInputNode_1775256565448_0.1537',
          inputText: userPreferences || 'No user preferences available',
          links: [],
          files: [],
        },
        {
          inputId: 'varInputNode_1775256789753_0.6104',
          inputText: `[Context]: ${JSON.stringify(contextData)}`,
          links: [],
          files: [],
        },
        {
          inputId: 'varInputNode_1775258591871_0.0136',
          inputText: flowSpaceId || '',
          links: [],
          files: [],
        },
      ];

      const response = await FluxService.FluxApiProduction(autoModelFlowId, {
        variableInputs,
      });

      if (
        response?.data?.message &&
        Array.isArray(response.data.message)
      ) {
        const processedOutput = response.data.message.find(
          (output: { outputId?: string; text?: string }) =>
            output.outputId && output.text && output.text.trim() !== '',
        );

        if (processedOutput?.text) {
          await applyAutoFindResult(processedOutput.text);
        }
      }
    } catch {
      setNotifications({
        message: 'Failed to auto-find model',
        type: 'error',
      });
    } finally {
      setIsAutoFinding(false);
    }
  };

  const onFileLinkSave = (saveOutput: boolean, fileType: string) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowTriggerData: checkExecuteTrigger(data, id),
      flowId,
      objectData: {
        saveOutput,
        fileType,
      },
      setSchema,
      setSaving,
    });
    setShowGenerateFileLinkModal(false);
  };

  const handleRun = (run: string) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      setSaving,
      id,
      flowId,
      objectData: {
        run,
      },
      setSchema,
    });
  };

  // Memory/Chat Mode handlers
  const onChangeMemory = (e: { target: { name: any; value: any } }) => {
    // For userSessionsChat, save directly to node data
    if (e.target.name === 'userSessionsChat') {
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        flowId,
        objectData: {
          userSessionsChat: e.target.value,
        },
        setSchema,
      });
      return;
    }

    setMemoryFormData({
      ...memoryFormData,
      [e.target.name]: e.target.value,
    });

    setIsChangingDirectly(true);
    setWhoIsChangingMemory({
      value: e.target.value,
      name: e.target.name,
    });
  };

  const onChangeMemoryToggle = async (e: { target: { checked: boolean } }) => {
    const newMemoryValue = e.target.checked;

    if (!newMemoryValue && flowId) {
      const edgesToRemove = edges.filter(
        edge => edge.source === id && edge.sourceHandle === 'output-memory',
      );

      if (edgesToRemove.length > 0) {
        const edgesToRemoveIds = edgesToRemove.map(edge => edge.id);

        setEdges(prevEdges => {
          return prevEdges.filter(edge => !edgesToRemoveIds.includes(edge.id));
        });

        nodeDataAutoSaveDynamic({
          setNodeState: setNodeState,
          id: id,
          flowId: flowId,
          changeType: 'delete-edges',
          setSchema,
          setSaving,
          newEdges: edges,
          deleteEdges: edgesToRemove,
          objectCallerData: [
            ...(user?.spaceObjects || []),
            ...(user?.objects || []),
          ],
        });
      }
    }

    setWhoIsChangingMemory({
      value: newMemoryValue,
      name: 'memory',
    });
    setIsChangingDirectly(true);
  };

  const getCurrentSessionMemories = (): any[] => {
    const currentSession = currentSessionId;
    const sessionsToUse =
      userSessionChat.length > 0
        ? userSessionChat
        : data.userSessionsChat || [];
    const sessionData = sessionsToUse.find(
      (chat: any) => chat.id === currentSession,
    );
    const memories = sessionData?.memories || [];
    return Array.isArray(memories) ? memories : [];
  };

  const transformMemory = (memory: any) => {
    if (memory.role !== 'user' || !memory.content) return memory;
    const firstLine = memory.content.split('\n', 1)[0];
    return { ...memory, content: firstLine };
  };

  const combineByWordsForContentData = (
    list: any[],
    limit: number,
    fromEnd: boolean,
  ): any[] => {
    let count = 0;
    const resultMemories: any[] = [];

    const start = fromEnd ? list.length - 1 : 0;
    const end = fromEnd ? -1 : list.length;
    const step = fromEnd ? -1 : 1;

    for (let i = start; i !== end; i += step) {
      if (count >= limit) break;

      const mem = transformMemory(list[i]);
      const content = mem.content || '';
      const words = content.split(/\s+/);
      const remaining = limit - count;

      if (words.length <= remaining) {
        resultMemories.push(mem);
        count += words.length;
      } else {
        const slice = fromEnd
          ? words.slice(words.length - remaining)
          : words.slice(0, remaining);
        const partialContent = slice.join(' ');
        resultMemories.push({ ...mem, content: partialContent });
        break;
      }
    }

    return fromEnd ? resultMemories.reverse() : resultMemories;
  };

  // Filter memories based on "Pass X Entries" settings (same logic as Small Memory)
  const filterMemoriesForContentData = (
    memories: any[],
    selectedType: string,
    selectedNumber: number,
    selectedIndex: number,
  ): any[] => {
    if (!memories || memories.length === 0) {
      return [];
    }

    let filteredMemories: any[] = [];

    // Apply filter based on selectedType (matching Small Memory logic from helpers.ts)
    if (selectedType === 'Load First X Entries') {
      // Take first selectedIndex entries (same as Small Memory outputsType[0])
      const newArray = [...memories];
      const spliceArrayByIndex = newArray.splice(0, selectedIndex);
      filteredMemories = spliceArrayByIndex.map(transformMemory);
    } else if (selectedType === 'Load Last X Entries') {
      // Take last selectedIndex entries (same as Small Memory outputsType[2])
      const newArray = [...memories];
      const spliceArrayByIndex = newArray.splice(
        newArray.length - selectedIndex,
        selectedIndex,
      );
      filteredMemories = spliceArrayByIndex.map(transformMemory);
    } else if (selectedType === 'Load First X Words') {
      filteredMemories = combineByWordsForContentData(
        memories,
        selectedNumber,
        false,
      );
    } else if (selectedType === 'Load Last X Words') {
      filteredMemories = combineByWordsForContentData(
        memories,
        selectedNumber,
        true,
      );
    } else if (selectedType === 'Load X Entries from Index') {
      // Load selectedNumber entries starting from selectedIndex
      filteredMemories = memories
        .slice(selectedIndex, selectedIndex + selectedNumber)
        .map(transformMemory);
    } else if (selectedType === 'Load All Entries') {
      // Return all memories
      filteredMemories = memories.map(transformMemory);
    }

    return filteredMemories;
  };

  // Remove memory entry from contentData
  const removeMemoryFromContentData = (contentData: any[]): any[] => {
    if (!contentData || !Array.isArray(contentData)) {
      return [];
    }
    return contentData.filter((item: any) => item.label !== 'Memory');
  };

  // Update contentData with memory entry
  const updateContentDataWithMemory = (
    contentData: any[],
    memoryArray: any[],
    nodeId: string,
  ): any[] => {
    // Remove existing memory entry
    let updatedContentData = removeMemoryFromContentData(contentData || []);

    // Add new memory entry if memories exist
    if (memoryArray && memoryArray.length > 0) {
      updatedContentData.push({
        label: 'Memory',
        id: nodeId,
        text: JSON.stringify(memoryArray),
      });
    }

    return updatedContentData;
  };

  const allMemories = useMemo(() => {
    return getCurrentSessionMemories();
  }, [data.userSessionsChat, userSessionChat, currentSessionId]);

  const currentMemories = useMemo(() => {
    const memories = allMemories;

    if (!memories || memories.length === 0) {
      return [];
    }

    const selectedType = data.selectedType || 'Load All Entries';
    const selectedNumber = data.selectedNumber || 50;
    const selectedIndex = data.selectedIndex || 5;

    if (selectedType === 'Load First X Entries') {
      return memories.slice(0, selectedIndex);
    } else if (selectedType === 'Load Last X Entries') {
      return memories.slice(-selectedIndex);
    } else if (selectedType === 'Load X Entries from Index') {
      return memories.slice(selectedIndex, selectedIndex + selectedNumber);
    } else if (selectedType === 'Load All Entries') {
      return [...memories];
    }

    return memories;
  }, [allMemories, data.selectedType, data.selectedNumber, data.selectedIndex]);

  // Clear memories function
  const clearMemories = () => {
    const currentSession = currentSessionId;
    const currentUserSessionsChat = data.userSessionsChat || [];

    // Find or create session entry
    let sessionIndex = currentUserSessionsChat.findIndex(
      (chat: any) => chat.id === currentSession,
    );

    let updatedUserSessionsChat;
    if (sessionIndex === -1) {
      // Session doesn't exist yet, nothing to clear
      return;
    } else {
      // Clear memories for the current session
      updatedUserSessionsChat = currentUserSessionsChat.map(
        (chat: any, index: number) => {
          if (index === sessionIndex) {
            return {
              ...chat,
              memories: [],
            };
          }
          return chat;
        },
      );
    }

    // Also remove memory entry from contentData
    const currentContentData = data.contentData || [];
    const updatedContentData = removeMemoryFromContentData(currentContentData);

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      changeType: 'clear-entries',
      objectData: {
        userSessionsChat: updatedUserSessionsChat,
        contentData: updatedContentData,
      },
      setSchema,
    });
  };

  const onChangeMemoryOnExecute = (e: {
    target: { name: string; checked: boolean };
  }) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      setSaving,
      objectData: {
        clearOnExecution: e.target.checked,
      },
      setSchema,
    });
  };

  const onMemoryFormSubmit = (formData: any) => {
    setMemoryFormData({
      ...formData,
    });

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      changeType: 'execute',
      objectData: {
        ...formData,
      },
      setSchema,
    });

    setEntriesAnchorEl(null);
  };

  useDebounceEffect(
    () => {
      if (isChangingDirectly) {
        setIsChangingDirectly(false);
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          changeType: 'changing-directly',
          objectData: {
            [whoIsChangingMemory.name]: whoIsChangingMemory.value,
          },
          setSchema,
        });

        setWhoIsChangingMemory({
          value: '',
          name: '',
        });
      }
    },
    [isChangingDirectly, whoIsChangingMemory],
    300,
  );

  const outputsType = [
    { name: 'Pass First X Entries', value: 'Load First X Entries' },
    { name: 'Pass First X Words', value: 'Load First X Words' },
    { name: 'Pass Last X Entries', value: 'Load Last X Entries' },
    { name: 'Pass Last X Words', value: 'Load Last X Words' },
  ];

  const isWords = watch('selectedType')?.includes('Words');

  const getWidth = () => {
    switch (data?.placement) {
      case 'Both':
        return '814px';

      case 'Prompt':
        return '829px';

      case 'Output':
        return '837px';

      default:
        return '814px';
    }
  };

  return (
    <>
      {showGenerateFileLinkModal && (
        <GenerateFileLinkModal
          onClose={() => setShowGenerateFileLinkModal(false)}
          onSave={onFileLinkSave}
          savedOutput={data.saveOutput}
          savedFileType={data.fileType}
        />
      )}

      <Box className={isLoading ? 'node' : ''}>
        <Node
          edges={edges}
          type={type}
          btnText="Run Prompt"
          onSubmit={onSubmit}
          isLoading={isLoading}
          data={data}
          id={id}
          selected={selected}
          onChangePlacement={onChangePlacement}
          handleRun={handleRun}
        >
          {!data?.collapsed && (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box display={'flex'} sx={{ alignItems: 'stretch' }}>
                <div>
                  <Box
                    p={'16px'}
                    ref={inputRef}
                    sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                  >
                    {aiGeneratorInputs.map(input => {
                      let height = 'auto';
                      if (input.name === 'prompt') {
                        height = inputSize?.height || '120px';
                      }
                      return (
                        <Box sx={{ flex: 1 }} key={input.handleId}>
                          <TagInput
                            onChange={onChange}
                            name={input.name}
                            key={input.name}
                            nodeId={id}
                            labelName={input.labelName}
                            dataConnected={data?.[input.name + 'Data'] || []}
                            placeholder={input.placeholder}
                            value={
                              formData?.[input.name as keyof typeof formData] ||
                              ''
                            }
                            placement={data?.placement}
                            nodeLabel={data?.label}
                            handleId={input.handleId}
                            isActive={isActiveEdge(
                              edges,
                              id,
                              input.handleId,
                              'target',
                            )}
                            findColor={findColor}
                            isCollapsed={input.isCollapsed}
                            onChangePlacement={onChangePlacement}
                            height={input.name === 'prompt' ? height : input.isCollapsed ? undefined : height}
                            width={input.name === 'prompt' ? inputSize?.width : undefined}
                          />
                        </Box>
                      );
                    })}

                    {data?.placement !== 'Output' && (
                      <Box
                        display={'flex'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        position={'relative'}
                        sx={{ marginTop: 'auto' }}
                      >
                        {getNodes()
                          .map(node => node.type)
                          .includes('dynamicSettingsNode') && (
                            <InputHandle
                              activeColor={findColor?.color}
                              handleId={'modelSettings'}
                              left={'-26px'}
                              top={'50%'}
                              isActive={isActiveEdge(
                                edges,
                                id,
                                'modelSettings',
                                'target',
                              )}
                              position={Position.Left}
                              type="target"
                            />
                          )}

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <Box
                            bgcolor={
                              user?.theme === 'dark' ? '#101828' : '#F9FAFB'
                            }
                            borderRadius={'4px'}
                            sx={{ display: 'flex', padding: '8px', gap: '8px' }}
                            onClick={() => setSettingsModalOpen(true)}
                          >
                            <AiIcon />
                            <Typography
                              fontSize={'12px'}
                              color="#667085"
                              variant="h6"
                            >
                              {changeModelBasedOnSystem(formData.AiSystem)}
                            </Typography>

                            <CaretDown size={'16px'} color="#667085" />
                          </Box>
                          <Box
                            component="button"
                            disabled={isAutoFinding}
                            onClick={handleAutoFind}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: 'none',
                              cursor: isAutoFinding ? 'default' : 'pointer',
                              opacity: isAutoFinding ? 0.6 : 1,
                              bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                              '&:hover': {
                                bgcolor: isAutoFinding
                                  ? undefined
                                  : user?.theme === 'dark' ? '#1D2939' : '#F2F4F7',
                              },
                            }}
                          >
                            {isAutoFinding && (
                              <CircularProgress size={12} sx={{ color: '#667085' }} />
                            )}
                            <Typography
                              fontSize={'12px'}
                              color="#667085"
                              variant="h6"
                            >
                              Auto-find
                            </Typography>
                          </Box>
                        </Box>

                        <WordCount
                          text={
                            checkWordCount()?.prompt +
                            checkWordCount()?.content +
                            checkWordCount()?.instruction +
                            checkWordCount()?.persona
                          }
                        />
                      </Box>
                    )}

                    <SettingsModal
                      onHide={() => setSettingsModalOpen(false)}
                      show={settingsModalOpen}
                      formData={formData}
                      onChange={onChange}
                      nodeData={data}
                    />
                  </Box>
                </div>

                <Box
                  p={'16px 24px 16px 16px'}
                  borderLeft={`1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    width: '100%',
                  }}
                >
                  <Box sx={{ flex: 1, marginBottom: '10px' }}>
                    <OutputTextarea
                      previewResponses={
                        data?.saveOutput ? [] : data?.previewResponses || []
                      }
                      previewIndex={data.previewIndex}
                      maxOutputHeight={maxOutputHeight}
                      value={getCurrentTextValue()}
                      activeHandle={isActiveEdge(edges, id, 'output', 'source')}
                      placement={data?.placement}
                      labelName={'Output'}
                      handleId={'output'}
                      nodeLabel={data?.label}
                      onPreview={onPreview}
                      onChangeOnExecute={onChangeOnExecute}
                      clearOnExecution={data.clearOnExecution || false}
                      findColor={findColor}
                      hasClearOnExecute={true}
                      minOutputHeight={'120px'}
                      onChangePlacement={onChangePlacement}
                      style={{
                        flex: 1,
                      }}
                      textareaHeight={'100%'}
                      nodeId={id}
                      inputSize={data?.outputSize}
                      collapsed={data?.collapsed || false}
                    />
                  </Box>

                  <OutputTextarea
                    isAccordion
                    previewResponses={[]}
                    previewIndex={0}
                    maxOutputHeight={maxOutputHeight}
                    value={data.logs}
                    activeHandle={isActiveEdge(edges, id, 'logs', 'source')}
                    placement={data?.placement}
                    labelName={'Logs'}
                    handleId={'logs'}
                    nodeLabel={data?.label}
                    onPreview={onPreview}
                    onChangeOnExecute={onChangeOnExecute}
                    clearOnExecution={data.clearOnExecution || false}
                    findColor={findColor}
                    minOutputHeight={'70px'}
                    hasClearOnExecute={false}
                    onChangePlacement={onChangePlacement}
                    style={{
                      flex: 1,
                    }}
                    textareaHeight={'100%'}
                    nodeId={id}
                    inputSize={data?.logsSize}
                    collapsed={data?.collapsed}
                  />

                  {data.saveOutput && (
                    <OutputTextarea
                      isAccordion
                      maxOutputHeight={maxOutputHeight}
                      value={data?.fileLink?.src || 'No Link generated'}
                      previewResponses={data?.previewResponses || []}
                      previewIndex={data.previewIndex || 0}
                      activeHandle={isActiveEdge(edges, id, 'output', 'source')}
                      placement={data?.placement}
                      labelName={'Link'}
                      handleId={'fileLink'}
                      nodeLabel={data?.label}
                      type={type}
                      onPreview={onPreview}
                      clearOnExecution={data.clearOnExecution || false}
                      findColor={findColor}
                      hasClearOnExecute={false}
                      textareaHeight={'100%'}
                      textareaPadding={'2px'}
                      nodeId={id}
                      inputSize={data?.fileLinkSize}
                      collapsed={data?.collapsed}
                    />
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'end',
                    }}
                  >
                    <IconButton
                      onClick={() => setShowGenerateFileLinkModal(true)}
                    >
                      <Link color="rgba(102, 112, 133, 1)" size={18} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  width: getWidth(),
                  borderTop: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'
                    }`,
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  boxSizing: 'border-box',
                  position: 'relative',
                }}
              >
                <Box
                  display={'flex'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  width={'100%'}
                >
                  <Box display={'flex'} alignItems={'center'} gap={'12px'} width={'100%'}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontWeight: 600,
                        backgroundColor:
                          user?.theme === 'dark' ? '#101828' : '#FFF',
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                        padding: '6px 12px',
                        borderRadius: '8px',
                        width: '100%',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                        <IconButton
                          onClick={() => setFooterExpanded(!footerExpanded)}
                          sx={{
                            padding: '0',
                            minWidth: '16px',
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                          }}
                        >
                          <CaretUp
                            size={16}
                            style={{
                              transform: footerExpanded
                                ? 'rotate(0deg)'
                                : 'rotate(180deg)',
                              transition: 'transform 0.2s ease',
                            }}
                          />
                        </IconButton>

                        <Typography
                          fontSize={'14px'}
                          fontWeight={600}
                          color={user?.theme === 'dark' ? '#D0D5DD' : '#101828'}
                        >
                          {currentSessionName}
                        </Typography>
                        <IconButtonTooltip title="Session for this memory. If you want to see memory from other sessions, select the session in the global node">
                          <Info
                            size={16}
                            color={
                              user?.theme === 'dark' ? '#98A2B3' : '#667085'
                            }
                          />
                        </IconButtonTooltip>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          justifyContent: 'flex-end',
                          width: '100%',
                        }}
                      >
                        <Typography
                          fontSize={'14px'}
                          color={user?.theme === 'dark' ? '#D0D5DD' : '#101828'}
                        >
                          Memory
                        </Typography>
                        <Switch
                          checked={data.memory || false}
                          onChange={onChangeMemoryToggle}
                        />
                        <Button
                          disabled={!data.memory}
                          onClick={event => {
                            if (data.memory) {
                              setEntriesAnchorEl(event.currentTarget);
                            }
                          }}
                          sx={{
                            backgroundColor: 'var(--Neutral-100, #F2F4F7)',
                            padding: '6px 10px',
                            borderRadius: '8px',
                            display: 'flex',
                            gap: '4px',
                            justifyContent: 'center',
                            color: 'var(--Neutral-800, #101828)',
                            fontSize: '14px',
                            fontWeight: '400',
                            opacity: data.memory ? 1 : 0.5,
                            cursor: data.memory ? 'pointer' : 'not-allowed',
                          }}
                        >
                          <Typography>
                            {memoryFormData.selectedType
                              .replace(
                                'X',
                                isWords
                                  ? String(memoryFormData.selectedNumber) || 'X'
                                  : String(memoryFormData.selectedIndex) || 'X',
                              )
                              .replace('Load', 'Pass')}
                          </Typography>
                          <CaretDown weight="fill" />
                        </Button>
                        <IconButtonTooltip
                          title={'Memory Settings'}
                          onClick={event => {
                            if (data.memory) {
                              setSettingsAnchorEl(event.currentTarget);
                            }
                          }}
                        >
                          <Gear
                            size={22}
                            color={
                              !data.memory
                                ? '#98A2B3'
                                : openSettingsPopover
                                  ? '#3650DE'
                                  : '#98A2B3'
                            }
                            style={{
                              opacity: data.memory ? 1 : 0.5,
                            }}
                          />
                        </IconButtonTooltip>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Collapse in={footerExpanded}>
                  <Box display={'flex'} flexDirection={'column'} gap={'12px'}>
                    {data.memory && (
                      <Box
                        sx={{
                          width: '100%',
                          boxSizing: 'border-box',
                          position: 'relative',
                        }}
                      >
                        <OutputTable
                          value={currentMemories}
                          entries={currentMemories}
                          showInputHandles={false}
                          labelName={'Store Content'}
                          findColor={findColor}
                          activeHandle={isActiveEdge(
                            edges,
                            id,
                            'output',
                            'source',
                          )}
                          handleId={'output-memory'}
                          isInputActive={false}
                          showRole={true}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            marginTop: '8px',
                          }}
                        >
                          <Button onClick={clearMemories}>Clear Entries</Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Collapse>

                {data.memory && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: '-11px',
                      top: footerExpanded ? '50%' : '45px',
                      width: '0',
                      height: '0',
                      zIndex: 10,
                      transition: 'top 0.3s ease',
                    }}
                  >
                    <InputHandle
                      activeColor={findColor?.color}
                      handleId={'output-memory'}
                      isActive={isActiveEdge(
                        edges,
                        id,
                        'output-memory',
                        'source',
                      )}
                      position={Position.Right}
                      right={0}
                      top={0}
                      type="source"
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Node>
      </Box>

      {/* Settings Popover */}
      <Popover
        open={openSettingsPopover}
        anchorEl={settingsAnchorEl}
        id={popOverSettingsId}
        onClose={() => setSettingsAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box
          sx={{
            width: '350px',
            border: '1px solid var(--Neutral-300, #D0D5DD)',
            padding: '16px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '22px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography fontSize="16px" fontWeight={600} lineHeight={'24px'}>
              Settings
            </Typography>
            <IconButton
              sx={{ padding: '4px' }}
              size="medium"
              onClick={() => setSettingsAnchorEl(null)}
            >
              <XCircle color="#667085" />
            </IconButton>
          </Box>
          <Box>
            <Typography fontSize="16px" fontWeight={600} lineHeight={'24px'}>
              Clear Execution
            </Typography>
            <Typography fontSize={'12px'} color={'#475467'}>
              Clear the stored entries on every flow execution
            </Typography>
            <Switch
              onChange={onChangeMemoryOnExecute}
              checked={memoryFormData.clearOnExecution}
            />
          </Box>
        </Box>
      </Popover>

      <Popover
        open={openEntriesPopover}
        anchorEl={entriesAnchorEl}
        id={popOverId}
        onClose={() => setEntriesAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box
          sx={{
            width: '350px',
            border: '1px solid var(--Neutral-300, #D0D5DD)',
            padding: '16px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '22px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography fontSize="16px" fontWeight={600} lineHeight={'24px'}>
              Entries to Pass
            </Typography>
            <IconButton
              sx={{ padding: '4px' }}
              size="medium"
              onClick={() => setEntriesAnchorEl(null)}
            >
              <XCircle color="#667085" />
            </IconButton>
          </Box>
          <Box>
            <Typography fontSize="16px" fontWeight={600} lineHeight={'24px'}>
              Which Entries to Pass
            </Typography>
            <Typography fontSize={'12px'} color={'#475467'}>
              Decide which of your stored entries to pass to other nodes.
            </Typography>
          </Box>
          <Box>
            <Select
              name="selectedType"
              value={watch('selectedType')}
              onChange={e => setValue('selectedType', e.target.value)}
              className="nodrag"
              size="small"
              sx={{
                '& .MuiList-root': {
                  backgroundColor:
                    user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                },
                '& .MuiSvgIcon-root': {
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                },
                borderRadius: '8px',
                border: 'none',
                boxShadow: 'none',
                '.MuiOutlinedInput-notchedOutline': { border: 0 },
                bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .Mui-selected': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                    },
                    backgroundColor:
                      user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  },
                },
              }}
            >
              {outputsType?.map(flow => (
                <MenuItem
                  key={flow.value}
                  value={flow.value}
                  sx={{
                    color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                  }}
                >
                  {flow.name}
                </MenuItem>
              ))}
              <MenuItem
                key="Load All Entries"
                value="Load All Entries"
                sx={{
                  color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                }}
              >
                Pass All Entries
              </MenuItem>
            </Select>
          </Box>
          {watch('selectedType') !== 'Load All Entries' && (
            <Box className="w-100">
              {isWords ? (
                <TextField
                  className="w-50 nodrag"
                  name="selectedNumber"
                  type="number"
                  label="Number of Words to pass"
                  value={watch('selectedNumber')}
                  onChange={e =>
                    setValue('selectedNumber', Number(e.target.value))
                  }
                  size="small"
                  inputProps={{
                    min: 1,
                  }}
                  InputProps={{
                    style: {
                      height: '50px',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                  }}
                  sx={{
                    width: '100%',
                    height: '50px',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    border: `2px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'
                      }`,
                  }}
                />
              ) : (
                <TextField
                  className="w-50 nodrag"
                  name="selectedIndex"
                  type="number"
                  label="Number of Entries to pass"
                  value={watch('selectedIndex')}
                  onChange={e =>
                    setValue('selectedIndex', Number(e.target.value))
                  }
                  inputProps={{
                    min: 1,
                  }}
                  InputProps={{
                    style: {
                      height: '50px',
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                  }}
                  sx={{
                    width: '100%',
                    height: '50px',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                    border: `2px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'
                      }`,
                  }}
                />
              )}
            </Box>
          )}
          <Button
            variant="contained"
            onClick={handleSubmit(onMemoryFormSubmit)}
          >
            Save
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default memo(AiTextGenerator);
